import Phaser from 'phaser';
import type { HitResult, JudgmentGrade, ScoreSnapshot } from '@/types';
import { computeAccuracy, computeComboMultiplier, computePointsForGrade } from '@/utils/scoreMath';
import type { ChartNote } from '@/types';

/**
 * Pure scoring state machine. Deliberately has zero knowledge of Phaser scenes or
 * rendering -- it only emits events ('judgment', 'reset') that a scene/HUD can listen
 * to. This keeps scoring logic testable and reusable (e.g. by a future replay system)
 * without dragging rendering concerns into it.
 */
export class ScoreManager extends Phaser.Events.EventEmitter {
  private score = 0;
  private combo = 0;
  private maxCombo = 0;
  private perfectCount = 0;
  private goodCount = 0;
  private missCount = 0;

  registerJudgment(note: ChartNote, grade: JudgmentGrade, diffMs: number): HitResult {
    let points = 0;
    if (grade === 'miss') {
      this.combo = 0;
      this.missCount++;
    } else {
      this.combo++;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      points = computePointsForGrade(grade, this.combo);
      this.score += points;
      if (grade === 'perfect') this.perfectCount++;
      else this.goodCount++;
    }

    const result: HitResult = { grade, lane: note.lane, diffMs, note, points };
    this.emit('judgment', result, this.getSnapshot());
    return result;
  }

  getCurrentMultiplier(): number {
    return computeComboMultiplier(this.combo);
  }

  getSnapshot(): ScoreSnapshot {
    return {
      score: this.score,
      combo: this.combo,
      maxCombo: this.maxCombo,
      perfectCount: this.perfectCount,
      goodCount: this.goodCount,
      missCount: this.missCount,
      accuracy: computeAccuracy(this.perfectCount, this.goodCount, this.missCount),
    };
  }

  reset(): void {
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.perfectCount = 0;
    this.goodCount = 0;
    this.missCount = 0;
    this.emit('reset');
  }
}
